import $ from 'jquery';
import '@classic/Shared/BootstrapTooltip';
import {
    Calendar,
    type CalendarOptions,
    type DayCellInfo,
    type DayHeaderInfo,
    type DatesSetInfo,
    type EventClickInfo,
    type EventDisplayInfo,
    type EventSourceFunc,
    type MoreLinkHandler,
    type MoreLinkInfo,
    type MountInfo,
} from 'fullcalendar';
import themePlugin from 'fullcalendar/themes/classic';
import dayGridPlugin from 'fullcalendar/daygrid';
import timeGridPlugin from 'fullcalendar/timegrid';
import listPlugin from 'fullcalendar/list';
// Light DOM: import the v7 stylesheets globally (no Shadow DOM, no :root->:host
// transform) so Monsido/Acquia scanners can traverse the rendered grid.
import 'fullcalendar/skeleton.css';
import 'fullcalendar/themes/classic/theme.css';
import 'fullcalendar/themes/classic/palette.css';
import './PageCalendar.scss';
import EventsRepository from '@classic/Components/EventsSlider/Persistence/EventsRepository';
import { type ViewSource } from '@classic/Components/EventsSlider/Types';
import { type PageCalendarBootstrap, pageCalendarBootstrapSchema } from './Schemas';
import { type PageCalendarEventProps, transformBackendEvent } from './Lib/EventTransform';
import { resolveEventLink } from './Lib/EventLink';
import { attachTooltip, buildAccessibleLabel, buildTooltipHtml } from './Lib/EventTooltip';
import { unregisterTooltipEscapeDismissal } from '@classic/Shared/TooltipEscape';
import { buildListRow } from './Lib/ListRow';
import { bindMoreLink, scheduleMorePopoverPosition, unbindMoreLink } from './Lib/MorePopover';
import { type CalendarToolbar, createToolbar } from './Lib/Toolbar';
import { formatCellDate, formatWeekColumnHeader } from './Lib/TimeFormat';

const FULL_CALENDAR_VIEW_SOURCE: ViewSource = 'full-calendar';

// FC keeps only the nav row; the view tabs and print/email/subscribe actions
// live in the custom toolbar (see Lib/Toolbar.ts). Arrows are split to the
// outer edges with the title centered, matching the legacy layout.
const NAV_TOOLBAR = {
    left: 'prevYear,prev',
    center: 'title',
    right: 'next,nextYear',
} as const;

// FC does not export the click handler's info type under its own name — the
// public `MoreLinkInfo` is the mount/content shape used by moreLinkWillUnmount.
type MoreLinkClickInfo = Parameters<MoreLinkHandler>[0];

const parseBootstrap = (): PageCalendarBootstrap | null => {
    const scriptEl = document.querySelector<HTMLScriptElement>(
        'script[type="application/json"][data-view-data-page-calendar]',
    );
    if (!scriptEl?.textContent) {
        console.error('Page calendar bootstrap data not found');
        return null;
    }
    let raw: unknown;
    try {
        raw = JSON.parse(scriptEl.textContent);
    } catch {
        console.error('Page calendar bootstrap data is not valid JSON');
        return null;
    }
    const result = pageCalendarBootstrapSchema.safeParse(raw);
    if (!result.success) {
        console.error('Invalid page calendar bootstrap data:', result.error);
        return null;
    }
    scriptEl.remove();
    return result.data;
};

const usesListLayout = (data: PageCalendarBootstrap): boolean =>
    data.isMealCalendar || data.accessibility || data.defaultView === 'list';

const resolveInitialView = (data: PageCalendarBootstrap): string => {
    if (usesListLayout(data)) {
        if (data.defaultDateView === 'agendaWeek') return 'listWeek';
        if (data.defaultDateView === 'agendaDay') return 'listDay';
        return 'listMonth';
    }
    if (data.defaultDateView === 'agendaWeek') return 'timeGridWeek';
    if (data.defaultDateView === 'agendaDay') return 'timeGridDay';
    return 'dayGridMonth';
};

const createEventSource = (
    repository: EventsRepository,
    calendarApiId: number,
): EventSourceFunc => async (fetchInfo, successCallback, failureCallback) => {
    try {
        const startDate = fetchInfo.startStr.split('T')[0];
        const endDate = fetchInfo.endStr.split('T')[0];
        const { events, siteTimezone } = await repository.fetchEvents(
            calendarApiId,
            startDate,
            endDate,
            FULL_CALENDAR_VIEW_SOURCE,
        );
        successCallback(events.map((event) => transformBackendEvent(event, siteTimezone)));
    } catch (error) {
        console.error('Error fetching calendar events:', error);
        failureCallback(error as Error);
    }
};

const createWeekDayHeader = (info: DayHeaderInfo): { domNodes: HTMLElement[] } => {
    const { weekday, day } = formatWeekColumnHeader(info.date);
    const wrap = document.createElement('span');
    wrap.className = 'pcal-week-header';

    const weekdayEl = document.createElement('span');
    weekdayEl.className = 'pcal-week-header__weekday';
    weekdayEl.textContent = weekday;

    const dayEl = document.createElement('span');
    dayEl.className = 'pcal-week-header__day';
    dayEl.textContent = day;

    wrap.append(weekdayEl, dayEl);
    return { domNodes: [wrap] };
};

const navigateToPrint = (calendar: Calendar, calendarApiId: number, email: boolean): void => {
    const view = calendar.view;
    const startUnix = Math.floor(view.currentStart.getTime() / 1000);
    const endUnix = Math.floor(view.currentEnd.getTime() / 1000);
    const params = new URLSearchParams({
        calendar_id: String(calendarApiId),
        start_date: String(startUnix),
        end_date: String(endUnix),
    });
    if (email) {
        params.set('email', 'true');
    }
    window.location.href = `/page/page_calendar_print?${params.toString()}`;
};

const buildCalendarOptions = (
    data: PageCalendarBootstrap,
    repository: EventsRepository,
    onDatesSet: (viewType: string) => void,
): CalendarOptions => {
    const { calendarApiId, isMealCalendar } = data;

    const listEventContent = (arg: EventDisplayInfo): { domNodes: HTMLElement[] } => {
        const props = arg.event.extendedProps as PageCalendarEventProps;
        return {
            domNodes: [buildListRow(props, arg.event.title, arg.event.id, isMealCalendar)],
        };
    };

    return {
        plugins: [themePlugin, dayGridPlugin, timeGridPlugin, listPlugin],
        initialView: resolveInitialView(data),
        height: 'auto',
        editable: false,
        selectable: false,
        lazyFetching: true,
        // Force block-bar rendering so timed events don't render as a leading dot
        // in the month grid (matches the Calendar Events component, FC #7567).
        eventDisplay: 'block',
        // v7 removed the default bold on day-of-week headers and uses obfuscated
        // class names; restore styling via a stable class hook.
        dayHeaderClass: 'ss-fc-day-header',
        // Inner cushion defaults to nowrap; a stable class lets stacked week
        // headers wrap instead of clipping the day number (CRO-14323).
        dayHeaderInnerClass: 'ss-fc-day-header-inner',
        // Tag the list-row "before" slot (the colored dot) so it can be hidden,
        // matching the dot-free grid rendering.
        listItemEventBeforeClass: 'pcal-list-before',
        // List day headers: put the weekday next to the date (e.g. "Monday,
        // June 8, 2026") and drop the redundant right-side weekday. The RC's
        // `listDayAltFormat: false` falls back to the default formatter instead
        // of suppressing the side text, so use a formatter that renders nothing.
        listDayFormat: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
        listDayAltFormat: () => '',
        dayMaxEvents: true,
        headerToolbar: NAV_TOOLBAR,
        // Keep the custom toolbar's view tabs in sync with the live view
        // (arrow nav, deep links, and tab clicks all flow through here).
        datesSet: (info: DatesSetInfo): void => {
            onDatesSet(info.view.type);
        },
        // Returning void leaves FC's default popover action in place; we only
        // need a hook to reposition it once it has opened. Bind the native
        // click on mount so we have the control even if jsEvent.currentTarget
        // is already null (CRO-14575).
        moreLinkDidMount: (info: MountInfo<MoreLinkInfo>): void => {
            bindMoreLink(info.el);
            if (info.el.getAttribute('aria-expanded') === 'true') {
                scheduleMorePopoverPosition(info.el);
            }
        },
        moreLinkClick: (info: MoreLinkClickInfo): void => {
            scheduleMorePopoverPosition(info.jsEvent);
        },
        moreLinkWillUnmount: (info: MountInfo<MoreLinkInfo>): void => {
            unbindMoreLink(info.el);
        },
        noEventsContent: 'No events occurred.',
        // Inject a visually-hidden full date into every day cell so NVDA browse
        // mode announces the date on empty cells. FC v7 also sets aria-label on
        // each gridcell; removing it here makes our span the single source of
        // the date text, which prevents VoiceOver from reading it twice (once
        // from aria-label, once from the span content).
        dayCellDidMount: (info: MountInfo<DayCellInfo>): void => {
            info.el.removeAttribute('aria-label');
            const span = document.createElement('span');
            span.className = 'pcal-sr-only';
            span.textContent = formatCellDate(info.date as Date);
            info.el.appendChild(span);
        },
        views: {
            // Stack weekday above the day number so week headers stay readable in
            // seven narrow mobile columns instead of clipping "Mon 9/7" (CRO-14323).
            timeGridWeek: { dayHeaderContent: createWeekDayHeader },
            listMonth: { eventContent: listEventContent },
            listWeek: { eventContent: listEventContent },
            listDay: { eventContent: listEventContent },
        },
        eventSources: [createEventSource(repository, calendarApiId)],
        eventClick: (info: EventClickInfo): void => {
            info.jsEvent.preventDefault();
            const props = info.event.extendedProps as PageCalendarEventProps;
            const href = resolveEventLink(props, info.event.id);
            if (href) {
                window.location.href = href;
            }
        },
        eventDidMount: (info: MountInfo<EventDisplayInfo>): void => {
            if (info.view.type.startsWith('list')) {
                return;
            }
            const props = info.event.extendedProps as PageCalendarEventProps;
            attachTooltip(
                info.el,
                buildTooltipHtml(props, info.event.title, calendarApiId),
                buildAccessibleLabel(props, info.event.title),
            );
        },
        eventWillUnmount: (info: MountInfo<EventDisplayInfo>): void => {
            if (!info.view.type.startsWith('list')) {
                unregisterTooltipEscapeDismissal(info.el);
            }
            $(info.el).tooltip('destroy');
        },
    };
};

const applyDeepLink = (calendar: Calendar, data: PageCalendarBootstrap): void => {
    if (data.deepLinkView === 'day' && data.deepLinkDate) {
        calendar.changeView('timeGridDay');
        calendar.gotoDate(data.deepLinkDate);
    }
};

const initialize = (): void => {
    const data = parseBootstrap();
    if (!data || data.calendarApiId <= 0) {
        return;
    }

    const container = document.getElementById(`full-page-calendar${data.calendarApiId}`);
    if (!container) {
        console.error('Page calendar container not found');
        return;
    }

    const repository = new EventsRepository();
    let calendar: Calendar | null = null;
    let toolbar: CalendarToolbar | null = null;

    toolbar = createToolbar({
        isMealCalendar: data.isMealCalendar,
        getCalendar: () => calendar,
        onPrint: (email: boolean): void => {
            if (calendar) {
                navigateToPrint(calendar, data.calendarApiId, email);
            }
        },
        onSubscribe: (): void => {
            window.location.href = `/page/subscribe?calendar_id=${data.calendarApiId}`;
        },
    });
    container.parentElement?.insertBefore(toolbar.element, container);

    calendar = new Calendar(
        container,
        buildCalendarOptions(data, repository, (viewType) => {
            // Only the day-grid views render the "+N more" control that needs to
            // escape its cell (see PageCalendar.scss).
            container.classList.toggle('pcal-view-daygrid', viewType.startsWith('dayGrid'));
            toolbar?.syncActive(viewType);
        }),
    );

    calendar.render();
    applyDeepLink(calendar, data);
};

$(() => {
    initialize();
});
